import {
  AppliedFilterInterface,
  BlockStack,
  Button,
  FilterInterface,
  InlineStack,
  LegacyStack,
} from '@shopify/polaris'
import { useCallback, useMemo } from 'react'

import { SellingPlanAnchor, SellingPlanAnchorType } from '../../common/anchor-models'
import { AnchorField, anchorTypeSellingPlanIntervals } from '../../common/AnchorsEditModal'
import { SellingPlanInterval } from '../../common/plan-models'
import { AnchorTypeField } from '../../common/ScheduleFields'
import { anchorLabel } from '../../subscription-page/ScheduleCard'
import { SubscriptionFilter } from '../api'

const intervalAnchorTypes: Record<SellingPlanInterval, SellingPlanAnchorType | undefined> = {
  [SellingPlanInterval.DAY]: undefined,
  [SellingPlanInterval.WEEK]: 'WEEKDAY',
  [SellingPlanInterval.MONTH]: 'MONTHDAY',
  [SellingPlanInterval.YEAR]: 'YEARDAY',
}

const newAnchor = (type: SellingPlanAnchorType): SellingPlanAnchor => ({
  type,
  day: 1,
  month: type === 'YEARDAY' ? 1 : null,
})

const serializeAnchor = (anchor: SellingPlanAnchor): string => {
  if (anchor.type === 'YEARDAY') {
    return `YEARDAY:${anchor.month ?? 1}-${anchor.day}`
  }
  return `${anchor.type}:${anchor.day}`
}

const serializeAnchors = (anchors: SellingPlanAnchor[]): string =>
  anchors.map(serializeAnchor).join(',')

const parseAnchor = (raw: string): SellingPlanAnchor | null => {
  const [type, rest] = raw.split(':')
  if (!type || !rest) {
    return null
  }
  if (type === 'WEEKDAY' || type === 'MONTHDAY') {
    const day = parseInt(rest)
    if (!Number.isFinite(day)) {
      return null
    }
    return { type, day, month: null }
  }
  if (type === 'YEARDAY') {
    const [monthStr, dayStr] = rest.split('-')
    const month = parseInt(monthStr ?? '')
    const day = parseInt(dayStr ?? '')
    if (!Number.isFinite(month) || !Number.isFinite(day)) {
      return null
    }
    return { type: 'YEARDAY', day, month }
  }
  return null
}

const parseAnchors = (raw?: string): SellingPlanAnchor[] => {
  if (!raw) {
    return []
  }
  return raw
    .split(',')
    .map(parseAnchor)
    .filter((a): a is SellingPlanAnchor => a !== null)
}

export const useAddAnchorsFilter = (
  filter: SubscriptionFilter,
  updateFilter: (newFilter: Partial<SubscriptionFilter>) => any,
  filters: FilterInterface[],
  appliedFilters: AppliedFilterInterface[]
) => {
  const anchors = useMemo(() => parseAnchors(filter.anchors), [filter.anchors])

  const currentType: SellingPlanAnchorType = anchors[0]?.type ?? 'WEEKDAY'
  const currentInterval = anchorTypeSellingPlanIntervals[currentType]

  const updateAnchors = useCallback(
    (next: SellingPlanAnchor[]) => {
      updateFilter({ anchors: next.length ? serializeAnchors(next) : undefined })
    },
    [updateFilter]
  )

  const handleTypeChange = useCallback(
    (interval: SellingPlanInterval) => {
      const type = intervalAnchorTypes[interval]
      if (!type) {
        return
      }
      updateAnchors([newAnchor(type)])
    },
    [updateAnchors]
  )

  const handleAnchorChange = (anchor: SellingPlanAnchor, i: number) => {
    const next = [...anchors]
    next[i] = anchor
    updateAnchors(next)
  }

  const handleAnchorRemove = (i: number) => {
    const next = [...anchors]
    next.splice(i, 1)
    updateAnchors(next)
  }

  const handleAnchorAdd = () => {
    updateAnchors([...anchors, newAnchor(currentType)])
  }

  filters.push({
    key: 'anchors',
    label: 'Anchor date',
    filter: (
      <BlockStack gap="100">
        <AnchorTypeField label="Anchor type" onChange={handleTypeChange} value={currentInterval} />
        {anchors.map((anchor, i) => (
          <InlineStack key={i} blockAlign="center" gap="200">
            <LegacyStack.Item fill>
              <AnchorField
                anchor={anchor}
                interval={currentInterval}
                onChange={(updated) => handleAnchorChange(updated as SellingPlanAnchor, i)}
              />
            </LegacyStack.Item>
            <Button onClick={() => handleAnchorRemove(i)} variant="primary" tone="critical">
              Remove
            </Button>
          </InlineStack>
        ))}
        <InlineStack align="end">
          <Button onClick={handleAnchorAdd} variant="plain">
            Add anchor
          </Button>
        </InlineStack>
      </BlockStack>
    ),
  })

  if (anchors.length > 0) {
    appliedFilters.push({
      key: 'anchors',
      label: `Anchor date: ${anchors.map((a) => anchorLabel(a, true)).join(', ')}`,
      onRemove: () => updateFilter({ anchors: undefined }),
    })
  }
}
